import {
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  Input,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure
} from '@chakra-ui/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { FormField } from '../../../../components/FormField'
import { Icon } from '../../../../components/Icon'
import { TableSkeletonRows } from '../../../../components/Loading'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../../components/paginator'
import { SearchableSelect } from '../../../../components/Select'
import { PAYMENT_STATUS, StatusBadge, statusOptions } from '../../../../components/StatusBadge'
import { TableEmptyRow, TableErrorRow } from '../../../../components/TableEmptyRow'
import { Tooltip } from '../../../../components/Tooltip'
import { buildAsaasPaymentUrl, formatApiDate, useAppToast, useHasRole } from '../../../../helpers'
import ContestsService from '../../../../services/Contests/ContestsService'
import {
  IContestApplication,
  IContestApplicationsFilter,
  IContestForSelect
} from '../../../../services/Contests/interfaces'
import ContestApplicationModal from '../ContestApplicationModal'

interface IApplicationsTabProps {
  /** Concurso escolhido, que mora na página: a aba é desmontada pelo `isLazy` ao trocar de aba. */
  contestName: string
  onContestFilterChange?: (contestName: string) => void
  /** Muda quando a página cria uma inscrição (o FAB de inscrição manual), e a lista recarrega. */
  reloadKey?: number
}

// Mantenha sincronizado com o enum do back: ContestController::cancelApplication
// e migrations de contest_applications.payment_status. Se o back adicionar um
// status novo (ex.: 'chargeback'), adicionar entrada aqui — caso contrário o
// front mostrará o slug cru.
const canCancel = (status: string) =>
  !['cancelled', 'refunded', 'cancel_pending', 'refund_pending', 'declined'].includes(status)

// Do registry, e não de uma cópia: a lista que estava digitada aqui não tinha `failed`, então
// não dava para filtrar por "Falhou".
const paymentStatusOptions = [{ value: '', label: 'Todos' }, ...statusOptions(PAYMENT_STATUS)]

export default function ApplicationsTab({ contestName, onContestFilterChange, reloadKey }: IApplicationsTabProps) {
  const modalState = useDisclosure()
  const { isOpen: isAlertOpen, onOpen: onAlertOpen, onClose: onAlertClose } = useDisclosure()
  const toast = useAppToast()
  const [searchParams] = useSearchParams()
  const urlFilterApplied = useRef(false)
  const contestService = useMemo(() => new ContestsService(), [])
  const hasRole = useHasRole()
  const [contestsApplications, setContestsApplications] = useState<IContestApplication[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [contests, setContests] = useState<IContestForSelect[]>([])
  const [applicationId, setApplicationId] = useState<number>(0)
  const [cancellingId, setCancellingId] = useState<number | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [totalPages, setTotalPages] = useState(1)
  const [filter, setFilter] = useState<IContestApplicationsFilter>({
    contest_name: contestName,
    payment_status: 'paid',
    applicant_name: '',
    page: 1,
    page_size: DEFAULT_PAGE_SIZE
  })

  const fetchContests = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const response = await contestService.listApplications(filter)
      setContestsApplications(response.data)
      setTotalPages(Math.ceil(response.total_filtered / filter.page_size))
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar as inscrições', error)
    } finally {
      setLoading(false)
    }
  }

  // Fetch contests for select on component mount
  useEffect(() => {
    contestService
      .getAllForSelect()
      .then((response) => {
        setContests(response)
      })
      .catch(() => {
        toast.error('Erro ao Listar Concursos!')
      })
  }, [])

  // Apply URL-based contest filter on component load
  useEffect(() => {
    if (contests.length === 0 || urlFilterApplied.current) {
      return
    }

    const contestSlug = searchParams.get('contest')
    if (!contestSlug) {
      return
    }

    // Find contest by slug (case-insensitive)
    const matchedContest = contests.find((contest) => contest.slug.toLowerCase() === contestSlug.toLowerCase())

    if (matchedContest) {
      setFilter((prevFilter) => ({
        ...prevFilter,
        contest_name: matchedContest.name,
        page: 1
      }))
      urlFilterApplied.current = true
    }
  }, [contests, searchParams])

  // Notify parent whenever contest_name changes, regardless of how it was set
  // (manual ReactSelect change, URL-based filter, or any other programmatic update).
  // This ensures the parent page always reflects the currently selected contest
  // and keeps action buttons that depend on `selectedContestFromActiveTab` in sync.
  useEffect(() => {
    onContestFilterChange?.(filter.contest_name)
  }, [filter.contest_name])

  // Debounce effect for filters
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchContests()
    }, 500)

    return () => clearTimeout(timeoutId)
  }, [filter, reloadKey])

  function handleAlertClose() {
    onAlertClose()
    setCancellingId(null)
  }

  function handleCancelConfirm() {
    if (cancellingId === null) return
    const targetId = cancellingId
    setIsCancelling(true)
    contestService
      .cancelApplication(targetId)
      .then((res) => {
        const isPending = res.payment_status === 'cancel_pending' || res.payment_status === 'refund_pending'
        const isRefund = res.payment_status === 'refunded' || res.payment_status === 'refund_pending'
        const toastTitle = isRefund
          ? 'Estorno solicitado'
          : isPending
            ? 'Cancelamento solicitado'
            : 'Cancelamento realizado'

        toast.success(toastTitle, isPending ? `${res.message} Aguarde a confirmação.` : res.message)

        // Optimistic update: reflete o novo status na linha imediatamente para
        // evitar que o usuário continue vendo 'paid' enquanto o fetch recarrega.
        setContestsApplications((prev) =>
          prev.map((app) => (app.id === targetId ? { ...app, payment_status: res.payment_status } : app))
        )

        handleAlertClose()
        fetchContests()
      })
      .catch((err) => {
        const status = err?.response?.status
        let msg = err?.response?.data?.error ?? 'Erro ao cancelar inscrição'
        if (status === 403) {
          msg = 'Você não tem permissão para esta ação.'
        }
        toast.error(msg)
      })
      .finally(() => setIsCancelling(false))
  }

  return (
    <>
      <Box mb={4}>
        <Flex alignItems="center">
          <FormField label="Concurso" mr={2}>
            <SearchableSelect
              isClearable
              placeholder="Selecione um concurso"
              entity="concurso"
              options={contests.map((contest) => ({ value: contest.name, label: contest.name }))}
              value={filter.contest_name}
              onChange={(contestName) => setFilter({ ...filter, contest_name: contestName, page: 1 })}
            />
          </FormField>

          <FormField label="Status de Pagamento" mr={2}>
            <SearchableSelect
              isClearable
              placeholder="Selecione um status de pagamento"
              entity="status"
              options={paymentStatusOptions}
              value={filter.payment_status}
              onChange={(paymentStatus) => setFilter({ ...filter, payment_status: paymentStatus, page: 1 })}
            />
          </FormField>

          <FormField label="Participante (Nome ou CPF)" mr={2}>
            <Input
              type="text"
              placeholder="Digite o nome ou CPF do participante"
              value={filter.applicant_name}
              onChange={(e) => setFilter({ ...filter, applicant_name: e.target.value, page: 1 })}
            />
          </FormField>
        </Flex>
      </Box>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>#</Th>
              <Th>Concurso</Th>
              <Th>Participante</Th>
              <Th>Qtd de Obras Submetidas</Th>
              <Th>Status de Pagamento</Th>
              <Th>Data da Inscrição</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={7} />
            ) : loadError ? (
              <TableErrorRow colSpan={7} entity="inscrições" feminine onRetry={fetchContests} />
            ) : contestsApplications.length === 0 ? (
              <TableEmptyRow colSpan={7} entity="inscrição" feminine />
            ) : (
              contestsApplications.map((application) => {
                return (
                  <Tr key={application.id}>
                    <Td>{application.id}</Td>
                    <Td>{application.contest_name}</Td>
                    <Td>
                      <Flex alignItems="center" gap={3}>
                        <Avatar size="sm" name={application.applicant_name} src={application.applicant_image_path} />
                        {application.applicant_name}
                      </Flex>
                    </Td>
                    <Td>{application.art_submissions_count}</Td>
                    <Td>
                      <Flex align="center" gap={2}>
                        <StatusBadge status={application.payment_status} statuses={PAYMENT_STATUS} />
                        {application.is_manual && (
                          <Tooltip label="Inscrição registrada manualmente no painel">
                            <Badge tabIndex={0} aria-label="Inscrição registrada manualmente no painel">
                              Manual
                            </Badge>
                          </Tooltip>
                        )}
                      </Flex>
                    </Td>
                    <Td>{formatApiDate(application.created_at)}</Td>
                    <Td>
                      <Button
                        colorScheme="blue"
                        size="sm"
                        mx="2"
                        onClick={() => {
                          setApplicationId(application.id)
                          modalState.onOpen()
                        }}
                      >
                        <Icon name="lni lni-eye" />
                      </Button>
                      {application.gateway_invoice_id && (
                        <Tooltip label="Abrir cobrança no Asaas">
                          <Button
                            as="a"
                            href={buildAsaasPaymentUrl(application.gateway_invoice_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            colorScheme="purple"
                            variant="outline"
                            size="sm"
                            mx="2"
                          >
                            <Icon name="lni lni-link" />
                          </Button>
                        </Tooltip>
                      )}
                      {canCancel(application.payment_status) &&
                        (() => {
                          const hasCancelPermission = hasRole(
                            application.is_free ? 'cancel_free_ticket_contests' : 'cancel_ticket_contests'
                          )
                          return (
                            <Tooltip
                              label={
                                application.is_free
                                  ? 'Sem permissão para cancelar tickets gratuitos'
                                  : 'Sem permissão para cancelar tickets pagos'
                              }
                              isDisabled={hasCancelPermission}
                            >
                              <span>
                                <Button
                                  colorScheme="red"
                                  size="sm"
                                  mx="2"
                                  isDisabled={!hasCancelPermission}
                                  onClick={() => {
                                    setCancellingId(application.id)
                                    onAlertOpen()
                                  }}
                                >
                                  <Icon name="lni lni-close" />
                                </Button>
                              </span>
                            </Tooltip>
                          )
                        })()}
                    </Td>
                  </Tr>
                )
              })
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={filter.page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setFilter({ ...filter, page })
        }}
        pageSize={filter.page_size}
        onPageSizeChange={(pageSize) => {
          setFilter({ ...filter, page_size: pageSize, page: 1 })
        }}
      />

      <ContestApplicationModal isOpen={modalState.isOpen} onClose={modalState.onClose} applicationId={applicationId} />

      <ConfirmDialog
        isOpen={isAlertOpen}
        onClose={handleAlertClose}
        onConfirm={handleCancelConfirm}
        action="delete"
        title="Cancelar inscrição"
        description="Tem certeza que deseja cancelar esta inscrição? Se o pagamento já foi realizado, um estorno será solicitado. Esta ação não pode ser desfeita."
        confirmLabel="Confirmar cancelamento"
        cancelLabel="Voltar"
        isLoading={isCancelling}
      />
    </>
  )
}
